/**
 * Monthly credit cycle
 * ====================
 *
 * A membership (World/{uid}/creditPlan) starts a cycle on activation.
 * The next refill is one month later in Europe/Vienna — not the next
 * calendar 1st. The scheduled job runs daily and only resets accounts
 * whose `nextDistribution` is due, so a plan activated on 27 Aug does
 * not get a second full allowance on 1 Sep.
 *
 * Unused credits are discarded (`rolloverRatio = 0`). Activation,
 * "Distribute Now" and the scheduled job all RESET to the plan
 * allowance; they never add on top of a leftover wallet.
 *
 * Team Dashboard "Premium" / emailPlan is a separate flag (daily send
 * quota). It must not invent a credit cycle in the cockpit.
 */

export const CREDIT_CYCLE_TZ = 'Europe/Vienna';

function viennaParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CREDIT_CYCLE_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(date);
  const num = (type) => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: num('year'),
    month: num('month'),
    day: num('day'),
    hour: num('hour'),
  };
}

function pad(n) {
  return String(n).padStart(2, '0');
}

/** UTC instant that is 00:00 on `year-month-day` in Europe/Vienna. */
export function viennaMidnightUtc(year, month, day) {
  for (const offset of ['+02:00', '+01:00']) {
    const instant = new Date(`${year}-${pad(month)}-${pad(day)}T00:00:00${offset}`);
    if (Number.isNaN(instant.getTime())) continue;
    const local = viennaParts(instant);
    if (local.year === year && local.month === month && local.day === day && local.hour === 0) {
      return instant;
    }
  }
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
}

export function addViennaMonths(from, months) {
  const { year, month, day } = viennaParts(from);
  const abs = year * 12 + (month - 1) + months;
  const y = Math.floor(abs / 12);
  const m = (abs % 12) + 1;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return viennaMidnightUtc(y, m, Math.min(day, lastDay));
}

/** 1st of the next calendar month, 00:00 Europe/Vienna. Legacy fallback only. */
export function nextCalendarCycle(from = new Date()) {
  const { year, month } = viennaParts(from);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return viennaMidnightUtc(nextYear, nextMonth, 1);
}

/** Next refill: one month after `from`, 00:00 Vienna. */
export function computeNextDistributionIso(from = new Date()) {
  return addViennaMonths(from, 1).toISOString();
}

export function isCreditCycleDue(creditPlan, now = new Date()) {
  if (!creditPlan || !creditPlan.isActive || !creditPlan.plan) return false;
  if (creditPlan.nextDistribution) {
    const next = new Date(creditPlan.nextDistribution);
    return !Number.isNaN(next.getTime()) && next.getTime() <= now.getTime();
  }
  return viennaParts(now).day === 1;
}

export function hasActiveMembership(creditPlan) {
  return !!(creditPlan && creditPlan.plan && creditPlan.isActive !== false);
}

/**
 * Next refill instant for display.
 * Prefer a still-future stored date, else lastDistribution + 1 month,
 * else nothing — the cockpit must not invent a calendar 1st for
 * accounts that only have an emailPlan / Premium flag.
 */
export function resolveNextCreditCycle(creditPlan, now = new Date()) {
  if (!hasActiveMembership(creditPlan)) return null;
  const stored = creditPlan.nextDistribution ? new Date(creditPlan.nextDistribution) : null;
  if (stored && !Number.isNaN(stored.getTime()) && stored.getTime() > now.getTime()) {
    return stored;
  }
  const last = creditPlan.lastDistribution ? new Date(creditPlan.lastDistribution) : null;
  if (last && !Number.isNaN(last.getTime())) {
    const fromLast = addViennaMonths(last, 1);
    if (fromLast.getTime() > now.getTime()) return fromLast;
  }
  return addViennaMonths(now, 1);
}

export function formatCreditCycleDate(date, { withYear = false } = {}) {
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: withYear ? 'numeric' : undefined,
    timeZone: CREDIT_CYCLE_TZ,
  });
}

export function daysUntil(date, now = new Date()) {
  return Math.max(0, Math.ceil((date.getTime() - now.getTime()) / 86400000));
}

/** True when lastDistribution started a window that has not ended yet. */
export function wasRefilledThisCycle(creditPlan, now = new Date()) {
  const last = creditPlan?.lastDistribution ? new Date(creditPlan.lastDistribution) : null;
  if (!last || Number.isNaN(last.getTime()) || last.getTime() > now.getTime()) return false;
  const next = creditPlan.nextDistribution
    ? new Date(creditPlan.nextDistribution)
    : addViennaMonths(last, 1);
  if (Number.isNaN(next.getTime())) return false;
  return now.getTime() < next.getTime();
}
